/**
 * @file Footer — the page footer bar — links and legal
 * @version 0.2.0
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { tokens, componentTokens } from '../../../theme/tokens';

// @render-mode: vite
// MUI/inline-style primitive (styling via inline `style={{}}` — no cva, no
// CSS Modules); the marker above opts into the visual regression Vite-spawn harness
// so `--component-tsx Footer.tsx` renders the actual component instead of an
// inline-HTML synthesized stub. See the visual regression tool.
import { Link } from '../Link';
// Heidrick wordmark — extracted verbatim from the Figma source./LogoBranding
// (208×11, 18 currentColor paths). The `?raw` query gives back the SVG markup so we can
// inject it via dangerouslySetInnerHTML — same pattern Icon.Standard uses. Type for the
// `?raw` query is declared in src/types/raw-svg.d.ts.
import logoBrandingSvg from '../../../assets/logo/logo-branding.svg?raw';
import { ReactComponent as LogoBranding } from '../../../assets/logo/logo-branding.svg';

/*
 * Footer — CATALOG
 *
 * Merged from two Figma COMPONENT_SETs both named "Footer" (intake 2026-05-18):
 * - dark default (the Figma source) —
 * Dark-Blue bg + gray-3-5 copyright text. Two tiers (`md` / `sm`).
 * - white link bar (the Figma source) —
 * White bg + darker-blue links separated by gray-1-20 dividers.
 * Three breakpoints (`md` / pop-up / `sm`).
 *
 * Variant axis:
 *   `dark`       — copyright footer (navy bg, white wordmark, copyright text)
 *   `light`      — full-width link bar (white bg, navy chrome)
 *   `popup`      — modal-context link bar (white bg, navy chrome)
 *   `assessment` — link bar for dark surfaces (transparent bg, white chrome,
 *                  grey-1-20 dividers). Consumer paints its own dark surface
 *                  behind the footer; the primitive doesn't paint a bg.
 *   `assessment-light` — link bar for LIGHT surfaces (transparent bg, navy
 *                  wordmark + dark-blue links, grey-1-20 dividers, plus a 1px
 *                  grey-1-20 top rule). The light-surface counterpart to
 *                  `assessment`; the consumer paints its own light surface behind.
 *
 * RESPONSIVE: Conforms to the design system framework §13
 * 2-tier responsive primitive — declares `sm · md`.
 * Auto-detect mechanism: JS `matchMedia` (Reflow type — same DOM, different
 * flex-direction/heights per tier). Theme-aligned breakpoint: switches at md
 * (992px) — viewports < 992 render the `sm` tier (mobile/stacked layout),
 * viewports ≥ 992 render the `md` tier (desktop/inline layout).
 *
 * Caller can also pin a tier with `forceLayout` for tests or pop-up contexts
 * where the parent's width doesn't match the viewport.
 *
 * HISTORY: Originally shipped 2026-05-18 with `forceBreakpoint: 'above'|'below'`
 * and a hardcoded 899px matchMedia query. Migrated to the framework's
 * `forceLayout: 'sm'|'md'` convention 2026-06-01 * Breakpoint moved from 899 → 991 (md - 1px) to align with the theme tokens.
 *
 * Logo: Figma source uses a `LogoBranding` component instance (208×11 wordmark)
 * which Heidrick design system does NOT yet ship as a primitive. This component
 * renders a styled text fallback in Avenir Heavy that approximates the wordmark
 * dimensions. When Logo primitive lands, swap the placeholder for the real asset.
 */

const dark = componentTokens.footer;

const Wordmark: React.FC<{ tone: 'dark' | 'light'; color?: string }> = ({ tone, color }) => (

  <LogoBranding
   aria-label="Heidrick & Struggles"
   role="img"
   style={{
     display: 'inline-block',
     width: 208,
     height: 11,
     flexShrink: 0,
     // `color` override wins when supplied (the wordmark SVG is all currentColor paths);
     // otherwise tone drives it: dark surface → white, light surface → navy fg.
     color: color ?? (tone === 'dark' ? '#FFFFFF' : dark.lightFg),
   }}
 />
);

export interface FooterLink {
 label: string;
 href?: string;
 target?: React.HTMLAttributeAnchorTarget;
 onClick?: () => void;
}

export interface FooterProps extends HTMLAttributes<HTMLElement> {
 variant?: 'dark' | 'light' | 'popup' | 'assessment' | 'assessment-light';
 /** Copyright text — used by the `dark` variant. Defaults to current-year Heidrick text. */
 copyright?: string;
 /** Link items — used by `light`, `popup`, and `assessment` variants. Defaults to Privacy / Legal / Help. */
 links?: FooterLink[];
 /**
 * Pin the layout tier (skips matchMedia auto-detect). Per the framework's
 * responsive-primitive convention Use in popups/
 * modals where viewport ≠ container, in tests, and in visual regression sidecars.
 * - `sm` = mobile/stacked layout (< 992)
 * - `md` = desktop/inline layout (≥ 992)
 */
 forceLayout?: 'sm' | 'md';
 /** Explicit width in px — overrides default `100%`. Used by visual regression sidecars to
 * pin the footer at the Figma source width (e.g. 1440 for md, 899 for sm). */
 width?: number | string;
 /** Render the root as a different element/component. Default `'footer'`. */
 as?: ElementType;
}

const DEFAULT_LINKS: FooterLink[] = [
 { label: 'Privacy Notice', href: '#privacy' },
 { label: 'Legal Notice', href: '#legal' },
 { label: 'Need Help?', href: '#help' },
];

export const Footer = forwardRef<HTMLElement, FooterProps>(function Footer(
 {
 variant = 'dark',
 copyright,
 links = DEFAULT_LINKS,
 forceLayout,
 width = '100%',
 as,
 className,
 style: styleOverride,
 ...rest
 },
 ref,
) {
 const Component = as ?? 'footer';
 // Evaluate at render time, not module-load time, so the year stays correct
 // across SSR runs and long-lived dev sessions that straddle a year boundary.
 const resolvedCopyright =
 copyright ??
 `Copyright © ${new Date().getFullYear()} Heidrick & Struggles International, Inc. All rights reserved. Reproduction or use without permission is prohibited.`;
 const [isBelow, setIsBelow] = React.useState<boolean>(false);

 React.useEffect(() => {
 if (forceLayout) {
 setIsBelow(forceLayout === 'sm');
 return;
 }
 if (typeof window === 'undefined') return;
 // Theme-aligned breakpoint: matches the md token (992px). The
 // `(max-width: 991px)` boundary covers everything below md, matching the
 // tier-range semantics in (`sm` = viewports < md).
 const mq = window.matchMedia('(max-width: 991px)');
 const update = () => setIsBelow(mq.matches);
 update();
 mq.addEventListener('change', update);
 return () => mq.removeEventListener('change', update);
 }, [forceLayout]);

 const isPopup = variant === 'popup';
 const isDark = variant === 'dark';
 const isAssessment = variant === 'assessment';
 // assessment-light is the light-surface link bar: navy chrome (like `light`) but
 // transparent bg + a top rule (like `assessment`, but for pale surfaces).
 const isAssessmentLight = variant === 'assessment-light';
 // Assessment variants paint no bg — the consumer's own surface shows through
 // (dark surface for `assessment`, pale surface for `assessment-light`).
 // Light/popup paint white; dark paints navy.
 const bg = isAssessment || isAssessmentLight ? 'transparent' : isDark ? dark.darkBg : dark.lightBg;
 const fg = isDark ? dark.darkFg : dark.lightFg;

 // --- dark variant: copyright footer ---
 if (isDark) {
 const horizontalPadding = isBelow ? 16 : 40;
 // Figma source heights: 72 above 900 (single row), 130 below 900 (stacked, copyright
 // wraps to 2 lines at the narrow width). Explicit height locks the visual regression bbox.
 const darkHeight = isBelow ? 130 : 72;
 // Figma's structure: Frame 2603 uses justify-content: space-between (logo left,
 // copyright right) with NO gap between them — there's no horizontal gap on the
 // outer footer either, just vertical padding. (The 10px "gap" on the outer Footer
 // applies only between vertical children; single-child stack so it's inert.)
 return (<Component
 ref={ref}
 className={className}
 style={{
 display: 'flex',
 flexDirection: isBelow ? 'column' : 'row',
 alignItems: isBelow ? 'flex-start' : 'center',
 justifyContent: isBelow ? 'flex-start' : 'space-between',
 width,
 height: darkHeight,
 padding: `24px ${horizontalPadding}px`,
 gap: isBelow ? 10 : 0,
 backgroundColor: bg,
 boxSizing: 'border-box',
 ...styleOverride,
 }}
 {...rest}
 >
 <Wordmark tone="dark" />
 <span
 style={{
 flex: '1 0 0',
 color: fg,
 fontFamily: tokens.font['family-avenir'],
 fontSize: 16,
 fontWeight: 300, // Figma reports 300 (Light); empirically tested 200 (per mapping rule) — got worse (5766 → 9093 px diff), so 300 stays. Mapping rule is glyph-specific, not universal.
 lineHeight: '24px',
 // Below 900 the copyright wraps as a full-width block under the wordmark — Figma source
 // is LEFT-aligned (ragged right). Above 900 it's pushed to the right edge of the row.
 textAlign: isBelow ? 'left' : 'right',
 }}
 >
 {resolvedCopyright}
 </span>
 </Component>);
 }

 // --- light + popup + assessment variants: link bar ---
 const linkGap = isBelow ? 8 : 16;
 // Popup variant uses 24px horizontal padding per Figma (vs 40px for full-width light).
 const horizontalPadding = isPopup ? 24 : isBelow ? 16 : 40;
 // Popup is the only Footer variant that has an explicit Figma height (84 vs 72 for light) —
 // because the inner Frame is 36 tall (vs light's 24-tall link row).
 const chassisHeight = isPopup ? 84 : undefined;
 // Light + assessment variants at sm stack wordmark on top of the links row;
 // popup keeps inline at any width (modal context).
 const stackLight = isBelow && !isPopup;
 // Assessment surface is dark — wordmark + links render white (Link primitive's
 // color="white" path). Light/popup render the navy chrome on white.
 const wordmarkTone = isAssessment ? 'dark' : 'light';
 const linkColor = isAssessment ? 'white' : 'dark';
 // Assessment surface chrome: the wordmark + footer links render in grey (#A9A9A9)
 // rather than the full-white default. (Link uses color="white" so its underline
 // tracks currentColor — the inline color override below cascades to it too.)
 const assessmentChrome = '#A9A9A9';
 return (<Component
 ref={ref}
 className={className}
 style={{
 display: 'flex',
 flexDirection: stackLight ? 'column' : 'row',
 alignItems: stackLight ? 'flex-start' : 'center',
 width,
 height: chassisHeight,
 padding: `24px ${horizontalPadding}px`,
 gap: stackLight ? 16 : 10,
 backgroundColor: bg,
 boxSizing: 'border-box',
 ...styleOverride,
 }}
 {...rest}
 >
 {/* assessment-light wordmark is Dark-Blue (#334255 = footer darkBg token), one
     step lighter than the navy used elsewhere — matches the fresh light screens. */}
 <Wordmark tone={wordmarkTone} color={isAssessment ? assessmentChrome : isAssessmentLight ? dark.darkBg : undefined} />
 {!stackLight && <div style={{ flex: '1 0 0' }} />}
 <nav style={{ display: 'flex', alignItems: 'center', gap: linkGap }} aria-label="Footer links">
 {links.map((link, i) => (<React.Fragment key={link.label}>
 {/* Heidrick Link primitive — Figma source locks Type=Text light,
 Size=Medium, State=Default for the footer nav. Color tracks the
 surface: `dark` for light/popup (navy on white), `white` for
 assessment (white on dark surface). The primitive handles
 hover/focus/active/disabled state styling automatically. */}
 <Link
 variant="text-light"
 color={linkColor}
 style={isAssessment ? { color: assessmentChrome } : undefined}
 href={link.href ?? '#'}
 target={link.target ?? (link.href && /^https?:\/\//i.test(link.href) ? '_blank' : undefined)}
 onClick={(e) => { if (!link.href) e?.preventDefault(); link.onClick?.(); }}
 >
 {link.label}
 </Link>
 {i < links.length - 1 && (<span
 aria-hidden
 style={{
 display: 'inline-block',
 width: 1,
 height: 14,
 backgroundColor: dark.lightSeparator,
 }}
 />)}
 </React.Fragment>))}
 </nav>
 </Component>);
});

Footer.displayName = 'Footer';
